import { useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers';
// Api
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
import { CREATE_STREAM } from 'api/streams/mutations';
// Types
import { CreateMatchupInput } from 'api/graphql-global-types';
import {
  CreateStream,
  CreateStream_createStream,
  CreateStreamVariables,
} from 'api/streams/types/CreateStream';
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import { StreamFields } from 'api/streams/fragments';
import { GetStreamExtended_stream_mentions } from 'api/streams/types/GetStreamExtended';
// Constants
import { TimeZoneOption } from 'constants/timeZone';
// Helpers
import { getTimeZoneObject } from 'helpers/timeZone';
import {
  formatHashtagInput,
  getStreamSaleInput,
  getStreamScheduleInput,
  REPEAT_STREAM_OPTIONS,
  RepeatStreamOption,
} from 'helpers/streams';
import { getProperErrorMessage } from 'helpers/errors';
import { formatMentionsInput } from 'helpers/mentions';
import { createEditStreamValidationSchema } from 'helpers/validation';
import { BoutDraft, getMatchcardInput } from 'helpers/matchcard';
import {
  buildStreamTicketEventInput,
  STREAM_TICKETS_DEFAULT_VALUES,
  StreamTicketsDraft,
  VENUE_STEP_FIELDS,
} from 'helpers/ticketEvents';
// Hooks
import { useOnboardingContext } from 'hooks';
import { usePartlySavedForm } from 'hooks/usePartlySavedForm';
import { usePublishTicketEvent } from 'hooks/usePublishTicketEvent';
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Components
import FullScreenStepperModal from 'components/common3/FullScreenStepperModal/FullScreenStepperModal';
import InviteAndPromoteModal from 'components/ProfileSetup/components/InviteAndPromoteModal/InviteAndPromoteModal';
import StreamDetailsStep from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/StreamDetailsStep/StreamDetailsStep';
import PricingStep from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/PricingStep/PricingStep';
import AdvancedOptionsStep from 'components/ProfileSetup/components/common/CreateStreamWizard/steps/AdvancedOptionsStep/AdvancedOptionsStep';
import StreamSummaryPanel from 'components/ProfileSetup/components/common/CreateStreamWizard/StreamSummaryPanel/StreamSummaryPanel';
import WizardStepIndicator from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/WizardStepIndicator/WizardStepIndicator';
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './CreateStreamSetup.module.scss';

export type FormInputs = {
  name: string | null;
  description: string;
  scheduleDate: Date | string;
  timeZone: TimeZoneOption;
  price: string;
  isFree: boolean;
  isHidden: boolean;
  image: File | null;
  previewImage: string;
  repeatsEveryEnum: RepeatStreamOption;
  hashtags?: string[];
  mentions?: GetStreamExtended_stream_mentions[] | null;
  matchcardEnabled: boolean;
  bouts: BoutDraft[];
  fanPollingEnabled: boolean;
  sharePickGraphicsEnabled: boolean;
  postEventContentHubEnabled: boolean;
  affiliateEnabled: boolean;
  affiliateCommissionPercent: string;
} & StreamTicketsDraft;

const STREAM_DETAILS_STEP = 1;
const PRICING_STEP = 2;
const ADVANCED_OPTIONS_STEP = 3;

// Validated when leaving a step; the full schema spans all steps.
const STEP_FIELDS: Record<number, string[]> = {
  [STREAM_DETAILS_STEP]: [
    'name',
    'description',
    'scheduleDate',
    'timeZone',
    'repeatsEveryEnum',
    'hashtags',
    'previewImage',
    'image',
    'isHidden',
  ],
  [PRICING_STEP]: [
    'price',
    'isFree',
    'affiliateEnabled',
    'affiliateCommissionPercent',
    'ticketsEnabled',
    ...VENUE_STEP_FIELDS,
    'doorsOpenTime',
    'tiers',
  ],
};

const CreateStreamSetup = () => {
  const {
    showCreateStreamModal,
    setCreateStreamDone,
    setShowCreateStreamModal,
  } = useOnboardingContext();

  const handleClose = () => {
    setShowCreateStreamModal(false);
  };

  // Closing Share & Promote leaves the creator where they were (e.g. My Streams)
  // instead of routing them away to the dashboard.
  const handleSuccess = () => {
    setCreateStreamDone(true);
    setShowInviteAthletes(false);
    handleClose();
  };

  const [
    streamData,
    setStreamData,
  ] = useState<CreateStream_createStream | null>(null);
  const [showInviteAthletes, setShowInviteAthletes] = useState(false);
  const [step, setStep] = useState(1);
  const [isScheduling, setIsScheduling] = useState(false);
  // A retry after a partial failure picks up what already exists instead of creating it twice.
  const createdStreamRef = useRef<CreateStream_createStream | null>(null);
  const { hasCreatedEvent, publishTicketEvent } = usePublishTicketEvent();
  const ticketingLaunched = useTicketingLaunched();
  const defaultTimeZone: TimeZoneOption = getTimeZoneObject(null);

  const defaultValues = {
    name: '',
    description: '',
    scheduleDate: '',
    timeZone: defaultTimeZone,
    repeatsEveryEnum: REPEAT_STREAM_OPTIONS[0],
    price: '',
    isFree: false,
    isHidden: false,
    previewImage: '',
    image: null,
    mentions: [],
    matchcardEnabled: false,
    bouts: [],
    fanPollingEnabled: false,
    sharePickGraphicsEnabled: false,
    postEventContentHubEnabled: false,
    ...STREAM_TICKETS_DEFAULT_VALUES,
    affiliateEnabled: false,
    affiliateCommissionPercent: '',
  };

  const methods = useForm<FormInputs>({
    resolver: yupResolver(createEditStreamValidationSchema(false)),
    defaultValues: defaultValues,
    shouldUnregister: false,
  });
  const partlySaved = usePartlySavedForm<FormInputs>(methods.reset);

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const [createStream] = useMutation<CreateStream, CreateStreamVariables>(
    CREATE_STREAM,
    {
      update(cache, { data }) {
        cache.modify({
          fields: {
            myStreams(existingStreams = {}) {
              const newStreamRef = cache.writeFragment({
                data: data?.createStream,
                fragment: StreamFields,
              });

              return {
                ...existingStreams,
                entities: [newStreamRef, ...existingStreams.entities],
              };
            },
          },
        });
      },
    }
  );

  // Matchups and fan engagement are paid-stream features: a free stream has no
  // Advanced Options step at all and schedules straight from Pricing & Tickets.
  // Watching every field re-renders the steps, so the summary and the ticket fields follow each change.
  const watchedValues = methods.watch();
  const hasAdvancedOptions = !watchedValues.isFree;
  const pricingStepLabel = ticketingLaunched
    ? 'Pricing & Tickets'
    : 'Pricing & Affiliate';
  const stepLabels = hasAdvancedOptions
    ? ['Stream Details', pricingStepLabel, 'Advanced Options']
    : ['Stream Details', pricingStepLabel];
  const totalStepsNumber = stepLabels.length;

  const createStreamFromValues = async (
    values: FormInputs,
    matchups: CreateMatchupInput[]
  ): Promise<CreateStream_createStream | null> => {
    const createUnregisteredContactInput =
      (values.mentions as any)?.filter((mention) => !mention.targetType) || [];

    const unregisterContactsData = await createUnregisteredContact({
      variables: {
        input: createUnregisteredContactInput,
      },
    });

    const newUnregisteredContacts =
      unregisterContactsData.data?.createUnregisteredContact?.map(
        (contact) => ({
          unregisteredContactId: contact.id,
        })
      ) || [];

    const oldMentions =
      values.mentions?.filter((mention) => mention.targetType) || [];
    const formatedOldMentions = formatMentionsInput(oldMentions);

    const createStreamResponse = await createStream({
      variables: {
        input: {
          ...getStreamSaleInput(values, matchups),
          isHidden: values.isHidden,
          hashtagInputs: values.hashtags
            ? formatHashtagInput(values.hashtags)
            : [],
          mentionsInputs: [...formatedOldMentions, ...newUnregisteredContacts],
          name: values.name,
          repeatingTitle: values.repeatsEveryEnum.value ? values.name : null,
          repeatsEveryEnum: values.repeatsEveryEnum.value,
          description: values.description,
          ...(values.image && { image: values.image }),
          ...getStreamScheduleInput(values.scheduleDate, values.timeZone),
        },
      },
    });

    return createStreamResponse?.data?.createStream || null;
  };

  const handleFormSubmit = methods.handleSubmit(
    async (values): Promise<void> => {
      // The resolver's cast values would not fill the form back in.
      const formState = methods.getValues();

      if (partlySaved.hasChangedSinceSave(formState)) {
        partlySaved.restoreSaved();
        return;
      }

      const { matchups, error: matchcardError } = getMatchcardInput(
        values.bouts,
        !values.isFree && values.matchcardEnabled
      );

      if (matchcardError) {
        showToast({ message: matchcardError, type: 'error' });
        return;
      }

      setIsScheduling(true);

      try {
        if (!createdStreamRef.current) {
          createdStreamRef.current = await createStreamFromValues(
            values,
            matchups
          );
        }

        const stream = createdStreamRef.current;

        if (!stream) {
          return;
        }

        if (ticketingLaunched && values.ticketsEnabled && !values.isFree) {
          await publishTicketEvent(() =>
            buildStreamTicketEventInput(values, stream.id)
          );
        }

        setStreamData(stream);
        showToast({
          message: 'Stream successfully created',
        });
        setShowInviteAthletes(true);
      } catch (error) {
        showToast({
          message: getProperErrorMessage(
            error,
            `Something went wrong. (${error?.message})`
          ),
          type: 'error',
        });

        if (createdStreamRef.current || hasCreatedEvent()) {
          partlySaved.markSaved(formState);
          partlySaved.restoreSaved();
        }
      } finally {
        setIsScheduling(false);
      }
    }
  );

  const goToNextStep = async () => {
    const isStepValid = await methods.trigger(STEP_FIELDS[step]);

    if (isStepValid) {
      setStep((currentStep) => currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (step === 1) {
      handleClose();
      return;
    }

    setStep((currentStep) => currentStep - 1);
  };

  const footer =
    step === totalStepsNumber
      ? {
          stepNumber: step,
          totalStepsNumber,
          showPrevButton: true,
          prevButtonProps: {
            onClick: goToPreviousStep,
            variant: 'secondary' as const,
            subVariant: 'outlined' as const,
          },
          submitButtonText: 'Schedule Stream',
          submitButtonProps: {
            onClick: handleFormSubmit,
            variant: 'primary' as const,
            loading: isScheduling,
          },
        }
      : {
          stepNumber: step,
          totalStepsNumber,
          showPrevButton: true,
          prevButtonProps: {
            onClick: goToPreviousStep,
            variant: 'secondary' as const,
            subVariant: 'outlined' as const,
          },
          submitButtonText: 'Continue',
          submitButtonProps: {
            onClick: goToNextStep,
            variant: 'primary' as const,
          },
        };

  return (
    <>
      {/* Stepper is hidden once the stream is created, so only Share & Promote is visible. */}
      <FullScreenStepperModal
        title="CREATE STREAM"
        open={showCreateStreamModal && !showInviteAthletes}
        onClose={handleClose}
        footer={footer}
      >
        <FormProvider {...methods}>
          <div className={styles.content}>
            <WizardStepIndicator steps={stepLabels} currentStep={step} />
            {step === STREAM_DETAILS_STEP && (
              <div className={styles.step}>
                <StreamDetailsStep />
              </div>
            )}
            {step === PRICING_STEP && <PricingStep />}
            {step === ADVANCED_OPTIONS_STEP && (
              <AdvancedOptionsStep aside={<StreamSummaryPanel />} />
            )}
          </div>
        </FormProvider>
      </FullScreenStepperModal>

      {streamData && showInviteAthletes && (
        <InviteAndPromoteModal
          show={showInviteAthletes}
          stream={streamData}
          onClose={handleSuccess}
        />
      )}
    </>
  );
};

export default CreateStreamSetup;
